/**
 * Item Price Tab Component
 * 
 * Shows Item Price list and Pricing Rules list for an Item.
 * Similar to QuickConnectionsTab but focused on pricing data.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useItemPriceListWithPagination } from '../api/itemPriceHooks';
import { usePricingRulesForItemWithPagination } from '../api/pricingRuleForItemHooks';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Plus, Calendar, User, Building2, Tag, Star, ShoppingCart, Layers, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SidebarButton } from '@/components/ui/sidebar-button';
import { buildNewDocRoute, buildListRoute } from '@/lib/config/docRoutes';
import type { FilterDescriptor } from '@/lib/erp';
import { useLanguage } from '@/i18n/useLanguage';
import { getUITranslation } from '@/lib/views/labels/translations';
import type { ItemPrice } from '@/lib/erp';
import type { PricingRule } from '@/lib/erp/pricingRules';
import { ItemPriceActionsModal } from './ItemPriceActionsModal';
import { PricingRuleActionsModal } from './PricingRuleActionsModal';

interface ItemPriceTabProps {
  doctype: string;
  name: string;
}

export function ItemPriceTab({ doctype, name }: ItemPriceTabProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // Resolve UI labels
  const uiLabels = React.useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      itemPrices: getUI('item_prices', 'Item Prices'),
      priceListEntriesForItem: getUI('price_list_entries_for_item', 'Price list entries for this item'),
      newItemPrice: getUI('new_item_price', 'New Item Price'),
      noItemPricesFound: getUI('no_item_prices_found', 'No item prices found'),
      createItemPrice: getUI('create_item_price', 'Create Item Price'),
      viewAllItemPrices: getUI('view_all_item_prices', 'View All Item Prices'),
      pricingRules: getUI('pricing_rules', 'Pricing Rules'),
      automaticPricingDiscountRules: getUI('automatic_pricing_discount_rules', 'Automatic pricing and discount rules for this item'),
      newPricingRule: getUI('new_pricing_rule', 'New Pricing Rule'),
      noPricingRulesFound: getUI('no_pricing_rules_found', 'No pricing rules found'),
      createPricingRule: getUI('create_pricing_rule', 'Create Pricing Rule'),
      viewAllPricingRules: getUI('view_all_pricing_rules', 'View All Pricing Rules'),
      buying: getUI('buying', 'Buying'),
      selling: getUI('selling', 'Selling'),
      per: getUI('per', 'per'),
      minPurchase: getUI('min_purchase', 'Min. purchase'),
      batch: getUI('batch', 'Batch'),
      validity: getUI('validity', 'Validity'),
      from: getUI('from', 'From'),
      to: getUI('to', 'To'),
      priority: getUI('priority', 'Priority'),
      conditions: getUI('conditions', 'Conditions'),
      failedToLoad: getUI('failed_to_load', 'Failed to load'),
    };
  }, [language]);
  
  // Pagination state for Item Prices
  const [itemPricePagination, setItemPricePagination] = React.useState({
    pageIndex: 0,
    pageSize: 50,
  });
  
  // Pagination state for Pricing Rules
  const [pricingRulePagination, setPricingRulePagination] = React.useState({
    pageIndex: 0,
    pageSize: 50,
  });
  
  // Reset pagination when item changes (prevents landing on blank page)
  React.useEffect(() => {
    setItemPricePagination(p => ({ ...p, pageIndex: 0 }));
    setPricingRulePagination(p => ({ ...p, pageIndex: 0 }));
  }, [name]);
  
  // Fetch Item Prices filtered by item_code
  const itemPriceFilters: FilterDescriptor[] = React.useMemo(() => [
    { field: 'item_code', operator: 'equals', value: name },
  ], [name]);
  
  const itemPriceQuery = useItemPriceListWithPagination({
    filterDescriptors: itemPriceFilters,
    pageIndex: itemPricePagination.pageIndex,
    pageSize: itemPricePagination.pageSize,
  });

  // Debug logging to trace data flow
  React.useEffect(() => {
    const raw = itemPriceQuery.data;
    let fullStructure: string | undefined = undefined;
    try {
      fullStructure = raw ? JSON.stringify(raw, null, 2) : undefined;
    } catch {
      fullStructure = '[unserializable]';
    }

    console.log('[ItemPriceTab] itemPriceQuery snapshot:', {
      isLoading: itemPriceQuery.isLoading,
      isError: itemPriceQuery.isError,
      error: itemPriceQuery.error,
      rawData: raw,
      hasDataProp: raw && 'data' in raw,
      dataArrayIsArray: Array.isArray((raw as any)?.data),
      dataLength: Array.isArray((raw as any)?.data) ? (raw as any).data.length : undefined,
      dataType: typeof (raw as any)?.data,
      fullStructure,
    });
  }, [itemPriceQuery.isLoading, itemPriceQuery.data, itemPriceQuery.error]);
  
  // Fetch Pricing Rules that apply to this item
  // Uses dedicated backend method that queries child table (Pricing Rule Item Code)
  // because Pricing Rules don't have a direct item_code field on the parent doctype
  const pricingRuleQuery = usePricingRulesForItemWithPagination({
    itemCode: name,
    pageIndex: pricingRulePagination.pageIndex,
    pageSize: pricingRulePagination.pageSize,
  });
  
  
  // State for Item Price Actions modal
  const [isItemPriceModalOpen, setIsItemPriceModalOpen] = React.useState(false);
  const [selectedItemPrice, setSelectedItemPrice] = React.useState<ItemPrice | null>(null);

  // State for Pricing Rule Actions modal
  const [isPricingRuleModalOpen, setIsPricingRuleModalOpen] = React.useState(false);
  const [selectedPricingRule, setSelectedPricingRule] = React.useState<PricingRule | null>(null);

  // Handle card/row click - open modal instead of navigating
  const handleItemPriceClick = React.useCallback((itemPrice: ItemPrice) => {
    setSelectedItemPrice(itemPrice);
    setIsItemPriceModalOpen(true);
  }, []);
  
  const handlePricingRuleClick = React.useCallback((pricingRule: PricingRule) => {
    setSelectedPricingRule(pricingRule);
    setIsPricingRuleModalOpen(true);
  }, []);
  
  // Format currency value
  const formatCurrency = React.useCallback((value: number | null | undefined, currency?: string | null): string => {
    if (value === null || value === undefined) return '-';
    // Format with thousands separator and 2 decimal places (accounting format)
    const formatted = Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currency ? `${formatted} ${currency}` : formatted;
  }, []);
  
  // Format date in readable format: "12-Dec-2026"
  const formatDate = React.useCallback((date: string | null | undefined): string => {
    if (!date) return '-';
    
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return String(date);
      
      const day = dateObj.getDate();
      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      const month = monthNames[dateObj.getMonth()];
      const year = dateObj.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch {
      return String(date);
    }
  }, []);
  
  return (
    <div className="pt-6 space-y-8">
      {/* Item Prices Section - Apple-style Card Layout */}
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground tracking-wide pl-4">{uiLabels.itemPrices}</h4>
            <p className="text-xs text-muted-foreground/70 mt-0.5 pl-4">
              {uiLabels.priceListEntriesForItem}
            </p>
          </div>
          <SidebarButton
            icon={<Plus className="h-5 w-5 sm:h-4 sm:w-4" />}
            onClick={() => {
              const url = buildNewDocRoute('Item Price', {
                link_doctype: doctype,
                link_name: name,
              });
              navigate(url);
            }}
          >
            {uiLabels.newItemPrice}
          </SidebarButton>
        </div>
        
        <div>
          {itemPriceQuery.isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-48 w-full rounded-sm" />
              ))}
            </div>
          ) : itemPriceQuery.error ? (
            <Alert variant="destructive">
              <AlertDescription>
                {uiLabels.failedToLoad} {uiLabels.itemPrices.toLowerCase()}
              </AlertDescription>
            </Alert>
          ) : !itemPriceQuery.data?.data || itemPriceQuery.data.data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <p className="text-sm mb-4">{uiLabels.noItemPricesFound}</p>
              <SidebarButton
                icon={<Plus className="h-5 w-5 sm:h-4 sm:w-4" />}
                onClick={() => {
                  const url = buildNewDocRoute('Item Price', {
                    link_doctype: doctype,
                    link_name: name,
                  });
                  navigate(url);
                }}
              >
                {uiLabels.createItemPrice}
              </SidebarButton>
            </div>
          ) : (
            <div className="space-y-3">
                {Array.isArray(itemPriceQuery.data?.data) &&
                  itemPriceQuery.data.data.map((raw) => {
                    const itemPrice = raw as ItemPrice & {
                      batch_no?: string | null;
                      customer?: string | null;
                      supplier?: string | null;
                      packing_unit?: number | string | null;
                    };

                    const customer = itemPrice.customer || undefined;
                    const supplier = itemPrice.supplier || undefined;
                    const batchNo = itemPrice.batch_no || undefined;

                    // NOTE: pack_qty does not exist in response – use packing_unit only
                    const packingUnit =
                      itemPrice.packing_unit !== null &&
                      itemPrice.packing_unit !== undefined
                        ? String(itemPrice.packing_unit)
                        : undefined;

                    const validityFrom = formatDate(itemPrice.valid_from);
                    const validityUpto = formatDate(itemPrice.valid_upto);
                    const hasValidity =
                      itemPrice.valid_from || itemPrice.valid_upto;
                    const validity = hasValidity
                      ? `${validityFrom} → ${validityUpto}`
                      : "—";

                return (
                  <div
                    key={itemPrice.name}
                    className="group relative rounded-2xl bg-white dark:bg-gray-900 border border-border/20 hover:border-border/40 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out cursor-pointer overflow-hidden backdrop-blur-sm"
                    onClick={() => handleItemPriceClick(itemPrice)}
                  >
                    <div className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-4 sm:gap-5">
                        {/* Left: Main Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            {/* Badges replacing dollar sign icon */}
                            <div className="flex flex-col gap-1.5 flex-shrink-0">
                              {((itemPrice as any).buying === 1 || (itemPrice as any).buying === true) && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-blue-500/10 border border-blue-500/20 shadow-[0_1px_2px_rgba(59,130,246,0.1)]">
                                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                  <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">
                                    {uiLabels.buying}
                                  </span>
                                </div>
                              )}
                              {((itemPrice as any).selling === 1 || (itemPrice as any).selling === true) && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/30 shadow-[0_1px_2px_rgba(16,185,129,0.1)]">
                                  <Tag className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                                  <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                                    {uiLabels.selling}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm sm:text-base font-semibold text-foreground truncate leading-tight">
                                {itemPrice.price_list || "Item Price"}
                              </h3>
                            </div>
                          </div>

                          {/* Price - Large and Prominent */}
                          <div className="mb-3">
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="text-2xl font-semibold text-foreground tabular-nums leading-tight">
                                {formatCurrency(itemPrice.price_list_rate, null)}
                              </span>
                              {itemPrice.currency && (
                                <span className="text-xs font-medium text-muted-foreground/50">
                                  {itemPrice.currency}
                                </span>
                              )}
                            </div>
                            {/* Per Unit - Minimal design */}
                            {itemPrice.uom && (
                              <div className="mt-1.5 flex items-center gap-2">
                                <span className="text-xs text-muted-foreground/50">{uiLabels.per}</span>
                                <Badge
                                  variant="outline"
                                  className="rounded-full px-2 py-0.5 text-[11px] bg-muted/30 border-border/40"
                                >
                                  <span className="font-medium text-foreground/70">{itemPrice.uom}</span>
                                </Badge>
                              </div>
                            )}
                          </div>

                           {/* Packing Unit - Minimal design */}
                           {packingUnit && (
                             <div className="mt-3 pt-2.5 border-t border-border/10">
                               <div className="flex items-center gap-2 text-xs text-muted-foreground/55">
                                 <ShoppingCart className="h-3.5 w-3.5" />
                                 <span className="font-medium text-foreground/75">{packingUnit}</span>
                                 <span className="text-muted-foreground/40">×</span>
                                 <span className="text-muted-foreground/55">{itemPrice.uom || 'units'}</span>
                                 <span className="text-muted-foreground/40">·</span>
                                 <span className="text-muted-foreground/45">{uiLabels.minPurchase}</span>
                               </div>
                             </div>
                           )}

                          {/* Details - Compact Horizontal List */}
                          {(batchNo || customer || supplier) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground/55 mt-3 pt-2.5 border-t border-border/8">
                              {batchNo && (
                                <div className="flex items-center gap-1.5">
                                  <Layers className="h-3.5 w-3.5 text-muted-foreground/40" />
                                  <span className="truncate max-w-[150px] font-medium text-foreground/70">{batchNo}</span>
                                </div>
                              )}
                              {customer && (
                                <div className="flex items-center gap-1.5">
                                  <User className="h-3.5 w-3.5 text-muted-foreground/40" />
                                  <span className="truncate max-w-[150px] font-medium text-foreground/70">{customer}</span>
                                </div>
                              )}
                              {supplier && (
                                <div className="flex items-center gap-1.5">
                                  <Building2 className="h-3.5 w-3.5 text-muted-foreground/40" />
                                  <span className="truncate max-w-[150px] font-medium text-foreground/70">{supplier}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Right: Additional Info */}
                        <div className="flex flex-col items-end gap-2.5 flex-shrink-0 min-w-[120px] sm:min-w-[140px]">
                          {/* Batch No (if available) */}
                          {batchNo && (
                            <div className="flex flex-col items-end gap-1 pt-2 border-t border-border/15 w-full">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
                                <Layers className="h-3 w-3" />
                                <span className="text-[10px] font-medium uppercase tracking-wide">{uiLabels.batch}</span>
                              </div>
                              <p className="text-xs font-medium text-foreground text-right leading-tight truncate max-w-full mt-0.5">
                                {batchNo}
                              </p>
                            </div>
                          )}

                          {/* Validity Info */}
                          {validity !== "—" && (
                            <div className="flex flex-col items-end gap-2 pt-2 border-t border-border/15 w-full">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60">
                                <Calendar className="h-3 w-3" />
                                <span className="text-[10px] font-medium uppercase tracking-wide">{uiLabels.validity}</span>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 justify-end">
                                {itemPrice.valid_from && (
                                  <Badge
                                    variant="outline"
                                    className="rounded-full px-3 py-1.5 text-sm flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-800/50 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                                  >
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">{uiLabels.from}</span>
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">{validityFrom}</span>
                                  </Badge>
                                )}
                                {itemPrice.valid_from && itemPrice.valid_upto && (
                                  <ArrowRight className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                )}
                                {itemPrice.valid_upto && (
                                  <Badge
                                    variant="outline"
                                    className="rounded-full px-3 py-1.5 text-sm flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-800/50 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                                  >
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">{uiLabels.to}</span>
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">{validityUpto}</span>
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                    );
                  })}
            </div>
          )}
        </div>
        
        {/* Show "View All" link if there are more items */}
        {itemPriceQuery.data && itemPriceQuery.data.data.length > 0 && (
          <div className="mt-6 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const baseUrl = buildListRoute('Item Price');
                const params = new URLSearchParams({
                  source_doctype: doctype,
                  source_name: name,
                });
                navigate(`${baseUrl}?${params.toString()}`);
              }}
            >
              {uiLabels.viewAllItemPrices}
            </Button>
          </div>
        )}
      </div>
      
      {/* Pricing Rules Section - Apple-style Card Layout */}
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground tracking-wide pl-4">{uiLabels.pricingRules}</h4>
            <p className="text-xs text-muted-foreground/70 mt-0.5 pl-4">
              {uiLabels.automaticPricingDiscountRules}
            </p>
          </div>
          <SidebarButton
            icon={<Plus className="h-5 w-5 sm:h-4 sm:w-4" />}
            onClick={() => {
              const url = buildNewDocRoute('Pricing Rule', {
                link_doctype: doctype,
                link_name: name,
              });
              navigate(url);
            }}
          >
            {uiLabels.newPricingRule}
          </SidebarButton>
        </div>
        
        <div>
          {pricingRuleQuery.isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-56 w-full rounded-sm" />
              ))}
            </div>
          ) : pricingRuleQuery.error ? (
            <Alert variant="destructive">
              <AlertDescription>
                {uiLabels.failedToLoad} {uiLabels.pricingRules.toLowerCase()}
              </AlertDescription>
            </Alert>
          ) : !pricingRuleQuery.data?.data || pricingRuleQuery.data?.data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <p className="text-sm mb-4">{uiLabels.noPricingRulesFound}</p>
              <SidebarButton
                icon={<Plus className="h-5 w-5 sm:h-4 sm:w-4" />}
                onClick={() => {
                  const url = buildNewDocRoute('Pricing Rule', {
                    link_doctype: doctype,
                    link_name: name,
                  });
                  navigate(url);
                }}
              >
                {uiLabels.createPricingRule}
              </SidebarButton>
            </div>
          ) : (
            <div className="space-y-4">
              {pricingRuleQuery.data?.data.map((pricingRule) => {
                const title = pricingRule.title || pricingRule.name;
                const validityFrom = formatDate(pricingRule.valid_from);
                const validityUpto = formatDate(pricingRule.valid_upto);

                const applyOn = pricingRule.apply_on || null;
                const applicableFor = pricingRule.applicable_for || null;
                const priority =
                  pricingRule.priority !== null && pricingRule.priority !== undefined
                    ? pricingRule.priority
                    : null;

                // Determine the action type and value
                const isDiscount = pricingRule.discount_percentage !== null && pricingRule.discount_percentage !== undefined;
                const isRate = pricingRule.rate !== null && pricingRule.rate !== undefined;
                const rateOrDiscount = pricingRule.rate_or_discount || null;
                
                const actionValue = isDiscount
                  ? `${Number(pricingRule.discount_percentage).toFixed(1)}%`
                  : isRate
                    ? formatCurrency(pricingRule.rate, pricingRule.currency)
                    : null;

                // Build condition summary
                const conditions: string[] = [];
                if (applyOn) conditions.push(`Applies on: ${applyOn}`);
                if (applicableFor) conditions.push(`For: ${applicableFor}`);
                if (pricingRule.customer) conditions.push(`Customer: ${pricingRule.customer}`);
                if (pricingRule.customer_group) conditions.push(`Customer Group: ${pricingRule.customer_group}`);
                if (pricingRule.item_group) conditions.push(`Item Group: ${pricingRule.item_group}`);
                if (pricingRule.brand) conditions.push(`Brand: ${pricingRule.brand}`);
                if (pricingRule.warehouse) conditions.push(`Warehouse: ${pricingRule.warehouse}`);
                if (pricingRule.company) conditions.push(`Company: ${pricingRule.company}`);

                // Action type determined for display logic
                // const actionType = isDiscount ? "discount" : isRate ? "rate" : null;

                return (
                  <div
                    key={pricingRule.name}
                    className="group relative rounded-xl bg-white dark:bg-gray-900 border border-border/20 hover:border-border/40 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out cursor-pointer overflow-hidden backdrop-blur-sm"
                    onClick={() => handlePricingRuleClick(pricingRule)}
                  >
                    <div className="p-6">
                      {/* Header Row: Title + Type Badges + Priority */}
                      <div className="flex items-start justify-between gap-4 mb-5">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-foreground leading-tight mb-3">
                            {title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2">
                            {((pricingRule as any).buying === 1 || (pricingRule as any).buying === true) && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-blue-500/10 border border-blue-500/20 shadow-[0_1px_2px_rgba(59,130,246,0.1)]">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">
                                  {uiLabels.buying}
                                </span>
                              </div>
                            )}
                            {((pricingRule as any).selling === 1 || (pricingRule as any).selling === true) && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/30 shadow-[0_1px_2px_rgba(16,185,129,0.1)]">
                                <Tag className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                                <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                                  {uiLabels.selling}
                                </span>
                              </div>
                            )}
                            {priority !== null && (
                              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/20 dark:border-amber-500/30 shadow-[0_1px_2px_rgba(245,158,11,0.1)]">
                                <Star className="h-3 w-3 text-amber-600 dark:text-amber-400 fill-amber-600 dark:fill-amber-400" />
                                <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                                  {uiLabels.priority} {priority}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Value - Prominent Right Side */}
                        {actionValue && (
                          <div className="flex flex-col items-end shrink-0 text-right">
                            <div className="flex items-baseline gap-2 mb-1.5">
                              <span className="text-2xl font-bold text-foreground tabular-nums leading-none">
                                {actionValue}
                              </span>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              {pricingRule.currency && !isDiscount && (
                                <span className="text-[10px] text-muted-foreground/60">
                                  {pricingRule.currency}
                                </span>
                              )}
                              {rateOrDiscount && (
                                <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">
                                  {rateOrDiscount}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Two-Column Layout: Conditions on Left, Validity on Right */}
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6">
                        {/* Left: Conditions */}
                        {conditions.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="h-px flex-1 bg-border/30" />
                              <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide px-2 shrink-0">
                                {uiLabels.conditions}
                              </span>
                              <div className="h-px flex-1 bg-border/30" />
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              {conditions.map((condition, idx) => {
                                const parts = condition.split(':');
                                const label = parts[0]?.trim() || '';
                                const value = parts.slice(1).join(':').trim() || '';
                                
                                return (
                                  <div
                                    key={idx}
                                    className="flex items-start gap-3 px-3 py-2 rounded-xs bg-gray-50/50 dark:bg-gray-950/30 border border-gray-200/30 dark:border-gray-800/30"
                                  >
                                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 mt-2 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-[10px] text-muted-foreground/60 uppercase tracking-wide font-medium mb-0.5">
                                        {label}
                                      </div>
                                      <div className="text-xs font-medium text-foreground/90 line-clamp-1">
                                        {value}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Right: Validity Period */}
                        {(pricingRule.valid_from || pricingRule.valid_upto) && (
                          <div className="flex flex-col items-end sm:items-start sm:border-l sm:border-border/20 sm:pl-5 shrink-0 min-w-0">
                            <div className="flex items-center gap-2 mb-4">
                              <Calendar className="h-4 w-4 text-muted-foreground/50" />
                              <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">
                                {uiLabels.validity}
                              </span>
                            </div>
                            <div className="flex flex-col items-end sm:items-start gap-2.5 w-full">
                              {pricingRule.valid_from && (
                                <Badge
                                  variant="outline"
                                  className="rounded-full px-4 py-2 text-sm flex items-center gap-2 bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-800/50 shadow-[0_1px_2px_rgba(0,0,0,0.05)] w-full sm:w-auto"
                                >
                                  <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">{uiLabels.from}</span>
                                  <span className="font-semibold text-gray-700 dark:text-gray-300">{validityFrom}</span>
                                </Badge>
                              )}
                              {pricingRule.valid_upto && (
                                <Badge
                                  variant="outline"
                                  className="rounded-full px-4 py-2 text-sm flex items-center gap-2 bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-800/50 shadow-[0_1px_2px_rgba(0,0,0,0.05)] w-full sm:w-auto"
                                >
                                  <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">{uiLabels.to}</span>
                                  <span className="font-semibold text-gray-700 dark:text-gray-300">{validityUpto}</span>
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Show "View All" link if there are more items */}
        {pricingRuleQuery.data?.data && pricingRuleQuery.data.data.length > 0 && (
          <div className="mt-6 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const baseUrl = buildListRoute('Pricing Rule');
                const params = new URLSearchParams({
                  source_doctype: doctype,
                  source_name: name,
                });
                navigate(`${baseUrl}?${params.toString()}`);
              }}
            >
              {uiLabels.viewAllPricingRules}
            </Button>
          </div>
        )}
      </div>

      {/* Item Price Actions Modal */}
      {selectedItemPrice && (
        <ItemPriceActionsModal
          open={isItemPriceModalOpen}
          onOpenChange={setIsItemPriceModalOpen}
          itemPrice={selectedItemPrice}
          itemCode={name}
        />
      )}

      {/* Pricing Rule Actions Modal */}
      {selectedPricingRule && (
        <PricingRuleActionsModal
          open={isPricingRuleModalOpen}
          onOpenChange={setIsPricingRuleModalOpen}
          pricingRule={selectedPricingRule}
          itemCode={name}
        />
      )}
    </div>
  );
}

